import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AssistantAskInput,
  AssistantSubject,
  FeatureSuggestion,
  NameSuggestion,
} from '@itsappy/shared';
import { ApiError } from '@/lib/api';
import {
  useAssistantAsk,
  useAssistantStatus,
  useAssistantThread,
  useClearAssistantThread,
  useSuggestFeatures,
  useSuggestNames,
} from '@/hooks/assistant';
import { IconButton, Modal, Spinner } from '@/components/ui';
import { CopyIcon, SendIcon, SparkleIcon } from '@/components/ui/icons';
import { AssistantAvatar } from '@/components/assistant/AssistantAvatar';
import { DictationButton } from '@/components/DictationButton';
import { Markdown } from '@/components/chat/Markdown';
import { ActionCard } from './ActionCard';
import { AssistantHost } from './host';
import { cn } from '@/lib/utils';

/** Server errors here are already written for a person; anything else is not. */
function message(error: unknown): string {
  return error instanceof ApiError ? error.message : 'The assistant could not answer just now';
}

function ErrorNote({ error }: { error: unknown }) {
  return (
    <p role="alert" className="text-xs text-red-400">
      {message(error)}
    </p>
  );
}

/**
 * The shell every assistant control sits in.
 *
 * It renders nothing at all while status is loading and nothing when the
 * deployment has no key — a surface that quietly isn't there beats one that
 * appears and then explains it cannot work. The exception is `explain`, for
 * places where its absence would be confusing rather than invisible.
 */
export function AssistantSection({
  title,
  explain = false,
  children,
}: {
  title: string;
  explain?: boolean;
  children: React.ReactNode;
}) {
  const { data: status } = useAssistantStatus();

  if (!status) return null;
  if (!status.enabled) {
    return explain ? (
      <p className="text-xs text-slate-500">
        Itsappy AI is unavailable — {status.reason ?? 'it is not configured for this workspace'}
        .
      </p>
    ) : null;
  }

  return (
    <section className="rounded-xl border border-surface-4 bg-surface-2/60 p-3">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-300">
        <SparkleIcon className="h-3.5 w-3.5 text-indigo-400" />
        {title}
      </h3>
      {children}
    </section>
  );
}

/* Answers never mention workspace members, so one shared empty map serves every
 * turn rather than allocating one per render. */
const NO_MENTIONS = new Map<string, string>();

/** What the general assistant is actually good at, one tap from being asked. */
const DEFAULT_STARTERS = [
  'Add a task for me to review the roadmap on Friday',
  'Draft an announcement for the launch and post it',
  'What is my team working on right now?',
  'Suggest names for a new project',
];

/**
 * The shape of one exchange, whoever is answering.
 *
 * Deliberately asymmetric: a question is short and belongs to the person who
 * asked it, so it sits in a bubble on their side; an answer is long, often a
 * list or a block of code, and reads far better given the full width than
 * squeezed into a facing bubble. Symmetric chat bubbles are a messaging
 * convention borrowed into a place it does not fit.
 */
export function Turn({
  turn,
  speaker,
  actions,
  children,
}: {
  turn: { id: string; role: 'user' | 'assistant'; content: string };
  speaker: string;
  actions?: React.ReactNode;
  /** Anything the answer proposes — rendered under it, inside the same turn. */
  children?: React.ReactNode;
}) {
  if (turn.role === 'user') {
    return (
      <div className="flex animate-slide-up justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand-500/15 px-3 py-2 text-sm text-slate-100">
          {turn.content}
        </p>
      </div>
    );
  }

  return (
    <div className="group animate-slide-up">
      <div className="mb-1 flex items-center gap-1.5">
        <AssistantAvatar size="xs" />
        <span className="text-xs font-medium text-slate-400">{speaker}</span>
      </div>

      {/* Answers are lists and code as often as prose, so they go through the
       * same renderer as every other message body. `names` is empty because
       * nothing here resolves to a person to mention. */}
      <Markdown body={turn.content} names={NO_MENTIONS} className="text-sm text-slate-200" />

      {children}

      {actions && (
        /* Revealed on hover or keyboard focus so a long thread is answers
         * rather than rows of buttons — but always shown where there is no
         * hover to reveal them with. */
        <div className="mt-1.5 flex gap-1 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100 max-sm:opacity-100">
          {actions}
        </div>
      )}
    </div>
  );
}

/** Three settling dots — the wait reads as someone composing, not as a stall. */
export function Thinking({ speaker }: { speaker: string }) {
  return (
    <div className="flex animate-fade-in items-center gap-2" role="status" aria-live="polite">
      <AssistantAvatar size="xs" thinking />
      <span className="flex items-end gap-0.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 animate-wave rounded-full bg-slate-500"
            style={{ animationDelay: `${i * 140}ms` }}
          />
        ))}
      </span>
      <span className="text-xs text-slate-500">{speaker} is thinking</span>
    </div>
  );
}

/**
 * What an empty conversation offers instead of a blank box.
 *
 * A cursor in an empty field is the hardest thing to answer, so these are the
 * things this assistant is actually good at — and since it can now create a
 * task or draft a post rather than only describe one, the first suggestions
 * are the ones that end in something done.
 */
export function StarterPrompts({
  prompts,
  onPick,
}: {
  prompts: string[];
  onPick: (prompt: string) => void;
}) {
  return (
    <div className="animate-fade-in">
      <div className="mx-auto mb-5 flex max-w-md flex-col items-center gap-2 py-3 text-center sm:py-5">
        <AssistantAvatar size="lg" />
        <p className="text-base font-semibold text-slate-100">What can I help move forward?</p>
        <p className="text-sm leading-5 text-slate-500">
          Itsappy AI knows your people, projects and channels — name them and it will line the
          work up for you to confirm.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onPick(prompt)}
            aria-label={`Ask Itsappy AI: ${prompt}`}
            className="group flex min-h-12 items-center gap-3 rounded-xl border border-surface-4 bg-surface-2 px-3.5 py-3 text-left text-sm leading-5 text-slate-300 transition-[background-color,border-color,color,transform] duration-150 hover:-translate-y-0.5 hover:border-brand-500/50 hover:bg-surface-3 hover:text-ink active:translate-y-0"
          >
            <span className="flex-1">{prompt}</span>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-3 text-slate-500 transition-colors group-hover:bg-brand-500/15 group-hover:text-brand-300">
              <SendIcon size="xs" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Renders its children only where Itsappy AI can actually answer. */
export function AssistantOnly({ children }: { children: React.ReactNode }) {
  const { data: status } = useAssistantStatus();
  return status?.enabled ? <>{children}</> : null;
}

/**
 * Suggests names for whatever is being created.
 *
 * The brief comes from the form the person is already filling in, so this asks
 * for nothing they have not typed — pressing it is the whole interaction, and
 * picking a suggestion writes it straight back into the field.
 */
export function NameSuggester({
  subject,
  brief,
  avoid = [],
  onPick,
}: {
  subject: AssistantSubject;
  brief: string;
  avoid?: string[];
  onPick: (suggestion: NameSuggestion) => void;
}) {
  const suggest = useSuggestNames();
  const [rejected, setRejected] = useState<string[]>([]);
  const ready = brief.trim().length >= 10;

  function run() {
    // Everything already shown joins `avoid`, so "Try again" moves on rather
    // than returning a reshuffle of the same six names.
    const seen = [...avoid, ...rejected, ...(suggest.data ?? []).map((s) => s.name)];
    setRejected(seen);
    suggest.mutate({ subject, brief: brief.trim(), count: 6, avoid: seen.slice(-50) });
  }

  return (
    <AssistantSection title="Suggest a name">
      <div className="space-y-2">
        <button
          type="button"
          onClick={run}
          disabled={!ready || suggest.isPending}
          className="btn btn-secondary btn-sm"
        >
          {suggest.isPending && <Spinner />}
          {suggest.data ? 'Try again' : 'Suggest names'}
        </button>

        {!ready && (
          <p className="text-xs text-slate-500">
            Describe what this is for first — a sentence is enough.
          </p>
        )}

        {suggest.isError && <ErrorNote error={suggest.error} />}

        {suggest.data && (
          <ul className="space-y-1">
            {suggest.data.map((suggestion) => (
              <li key={suggestion.name}>
                <button
                  type="button"
                  onClick={() => onPick(suggestion)}
                  className="w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-surface-3"
                >
                  <span className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">{suggestion.name}</span>
                    {suggestion.key && (
                      <span className="chip text-[10px] uppercase">{suggestion.key}</span>
                    )}
                  </span>
                  {suggestion.rationale && (
                    <span className="mt-0.5 block text-xs text-slate-400">
                      {suggestion.rationale}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AssistantSection>
  );
}

/**
 * `NameSuggester` for screens with no brief already on them.
 *
 * Renaming a workspace is the case this exists for: the form holds a name and
 * nothing that describes the company, so the assistant has to ask. The brief
 * is local and never saved — it is a prompt, not a field.
 */
export function NameSuggesterWithBrief({
  subject,
  prompt,
  avoid,
  onPick,
}: {
  subject: AssistantSubject;
  prompt: string;
  avoid?: string[];
  onPick: (suggestion: NameSuggestion) => void;
}) {
  const [brief, setBrief] = useState('');
  const { data: status } = useAssistantStatus();

  if (!status?.enabled) return null;

  return (
    <div className="space-y-2">
      <div>
        <label className="label" htmlFor={`brief-${subject}`}>
          {prompt}
        </label>
        <textarea
          id={`brief-${subject}`}
          rows={2}
          className="input resize-none"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="A remote design studio working with early-stage startups"
        />
      </div>
      <NameSuggester subject={subject} brief={brief} avoid={avoid} onPick={onPick} />
    </div>
  );
}

/**
 * Suggests features to build, and hands back the ones that were ticked.
 *
 * Selection is deliberate rather than all-or-nothing: a list of eight
 * suggestions usually contains three anyone wants, and adding the other five
 * to a board is worse than adding none.
 */
export function FeatureSuggester({
  brief,
  subject = 'project',
  existing = [],
  onAdd,
  addLabel = 'Add selected',
  busy = false,
}: {
  brief: string;
  subject?: AssistantSubject;
  existing?: string[];
  onAdd: (features: FeatureSuggestion[]) => void;
  addLabel?: string;
  busy?: boolean;
}) {
  const suggest = useSuggestFeatures();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const ready = brief.trim().length >= 10;

  function toggle(title: string) {
    setPicked((current) => {
      const next = new Set(current);
      if (!next.delete(title)) next.add(title);
      return next;
    });
  }

  function run() {
    setPicked(new Set());
    suggest.mutate({
      brief: brief.trim(),
      subject,
      count: 8,
      existing: existing.slice(0, 100),
    });
  }

  const chosen = (suggest.data ?? []).filter((f) => picked.has(f.title));

  return (
    <AssistantSection title="Suggest features">
      <div className="space-y-2">
        <button
          type="button"
          onClick={run}
          disabled={!ready || suggest.isPending}
          className="btn btn-secondary btn-sm"
        >
          {suggest.isPending && <Spinner />}
          {suggest.data ? 'Suggest more' : 'Suggest features'}
        </button>

        {!ready && (
          <p className="text-xs text-slate-500">
            Describe what you are building first — a sentence is enough.
          </p>
        )}

        {suggest.isError && <ErrorNote error={suggest.error} />}

        {suggest.data && (
          <>
            <ul className="space-y-1">
              {suggest.data.map((feature) => (
                <li key={feature.title}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface-3',
                      picked.has(feature.title) && 'bg-surface-3',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={picked.has(feature.title)}
                      onChange={() => toggle(feature.title)}
                    />
                    <span className="min-w-0">
                      <span className="flex items-baseline gap-2">
                        <span className="text-sm font-medium">{feature.title}</span>
                        <span className="chip text-[10px] uppercase">{feature.effort}</span>
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-400">
                        {feature.description}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => onAdd(chosen)}
              disabled={!chosen.length || busy}
              className="btn btn-primary btn-sm"
            >
              {busy && <Spinner />}
              {addLabel}
              {chosen.length > 0 && ` (${chosen.length})`}
            </button>
          </>
        )}
      </div>
    </AssistantSection>
  );
}

/**
 * A textarea that grows with what is in it, up to a point.
 *
 * A fixed two-row box is wrong in both directions: it wastes half the dialog
 * on one-line questions and hides the end of a pasted paragraph. Ten rows is
 * where growing stops being helpful and starts eating the conversation.
 */
function useAutoGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const field = ref.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 200)}px`;
  }, [value]);
  return ref;
}

/**
 * The general assistant, as a conversation.
 *
 * The transcript is what the server stored rather than what this component
 * remembers: answers are generated with the whole thread as context, so a
 * local idea of the conversation could show something never actually said.
 *
 * Answers can arrive with something to do attached, which is what turns this
 * from a box that writes paragraphs into one that gets work started. The
 * proposal renders inside the turn that made it and does nothing until it is
 * confirmed.
 *
 * `onPost` is what turns an answer from something you read into something you
 * can use — supplied by whichever screen has somewhere to put it.
 */
export function AssistantAsk({
  context,
  chat,
  placeholder,
  starters = DEFAULT_STARTERS,
  onPost,
  postLabel = 'Post to channel',
  onDismiss,
}: {
  context?: string;
  /** A conversation the server should read before answering, and how far back. */
  chat?: AssistantAskInput['chat'];
  placeholder?: string;
  starters?: string[];
  onPost?: (text: string) => void;
  postLabel?: string;
  /**
   * Closes the modal this is shown in, when it is shown in one. Followed links
   * call it first: see {@link AssistantHost}.
   */
  onDismiss?: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  /**
   * The question being answered right now.
   *
   * The transcript only gains the question once the answer has been written,
   * which left the asker watching three dots with no record of what they
   * asked. This holds their words on screen until the real turn arrives to
   * replace them.
   */
  const [pending, setPending] = useState<string | null>(null);
  const thread = useAssistantThread();
  const ask = useAssistantAsk();
  const clear = useClearAssistantThread();
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useAutoGrow(prompt);
  const host = useMemo(() => ({ dismiss: onDismiss }), [onDismiss]);

  const turns = useMemo(() => thread.data ?? [], [thread.data]);
  const empty = turns.length === 0 && !ask.isPending && !pending;

  useEffect(() => {
    endRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }, [turns.length, ask.isPending]);

  useEffect(() => {
    // Opening a desktop dialog should be ready to type. Avoid forcing the
    // software keyboard open merely because the surface mounted on a phone.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      inputRef.current?.focus();
    }
  }, [inputRef]);

  // Handing over to the real turn rather than clearing on completion: the
  // thread refetches after the answer lands, and dropping the placeholder any
  // earlier makes the question blink out and back again.
  useEffect(() => {
    if (pending && turns.some((turn) => turn.role === 'user' && turn.content === pending)) {
      setPending(null);
    }
  }, [turns, pending]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || ask.isPending) return;
    setPrompt('');
    setPending(trimmed);
    ask.mutate(
      { prompt: trimmed, context, chat },
      {
        // Hand it back on failure rather than losing what they typed.
        onError: () => {
          setPrompt(trimmed);
          setPending(null);
        },
      },
    );
  }

  async function copy(text: string, id: string) {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    window.setTimeout(() => setCopied((current) => (current === id ? null : current)), 1500);
  }

  return (
    <AssistantHost.Provider value={host}>
      <div className="flex flex-col gap-3">
        {/* A floor under the empty conversation, so answering the first question
         * does not shove the composer down the screen. */}
        <div className="min-h-[14rem] max-h-[min(58vh,30rem)] space-y-5 overflow-y-auto overscroll-contain pr-0.5">
          {empty && <StarterPrompts prompts={starters} onPick={send} />}

          {turns.map((turn) => (
            <Turn
              key={turn.id}
              turn={turn}
              speaker="Itsappy AI"
              actions={
                turn.role === 'assistant' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void copy(turn.content, turn.id)}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors duration-150 hover:bg-surface-3 hover:text-ink"
                    >
                      <CopyIcon size="xs" />
                      {copied === turn.id ? 'Copied' : 'Copy'}
                    </button>
                    {onPost && (
                      <button
                        type="button"
                        onClick={() => onPost(turn.content)}
                        className="rounded-md px-2 py-1 text-xs text-slate-400 transition-colors duration-150 hover:bg-surface-3 hover:text-ink"
                      >
                        {postLabel}
                      </button>
                    )}
                  </>
                ) : undefined
              }
            >
              {turn.action && (
                <ActionCard
                  action={turn.action}
                  messageId={turn.id}
                  result={turn.actionResult}
                />
              )}
            </Turn>
          ))}

          {pending && (
            <div className="flex animate-slide-up justify-end">
              <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand-500/15 px-3 py-2 text-sm text-slate-100">
                {pending}
              </p>
            </div>
          )}

          {ask.isPending && <Thinking speaker="Itsappy AI" />}
          <div ref={endRef} />
        </div>

        {ask.isError && <ErrorNote error={ask.error} />}

        {/* The composer is one object rather than a field with a button beside
         * it: the send control belongs to the message, and putting it inside
         * keeps the dialog's bottom edge quiet as the field grows. */}
        <div className="rounded-2xl border border-surface-4 bg-surface-2 transition-colors focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
          <div className="flex items-end gap-2 p-2">
            <textarea
              ref={inputRef}
              id="assistant-prompt"
              rows={1}
              enterKeyHint="send"
              aria-label="Message Itsappy AI"
              className="min-h-11 flex-1 resize-none bg-transparent px-2 py-2.5 text-base leading-6 text-slate-100 placeholder:text-slate-500 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:min-h-9 sm:py-1.5 sm:text-sm sm:leading-5"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                turns.length
                  ? 'Ask a follow-up…'
                  : (placeholder ?? 'Ask or say what you want done…')
              }
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — as everywhere else here.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  send(prompt);
                }
              }}
            />
            {/* Dictation, where the deployment can transcribe — the phone's
              assistant composer has had this and the web's had not, which
              made speaking to Itsappy AI a thing you could only do away from
              your desk. Renders nothing when STT is unconfigured, so this is
              a microphone or an unchanged composer, never a dead button. */}
            <DictationButton
              onTranscript={(text) =>
                // Appended, not replaced: dictation is a keystroke substitute,
                // and a second thought spoken after typing half a sentence
                // should finish it rather than wipe it.
                setPrompt((current) => (current.trim() ? `${current.trim()} ${text}` : text))
              }
              disabled={ask.isPending}
            />
            <button
              type="button"
              onClick={() => send(prompt)}
              disabled={!prompt.trim() || ask.isPending}
              aria-label="Send"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-600 text-white transition-[background-color,transform] hover:bg-brand-500 active:scale-95 disabled:bg-surface-4 disabled:text-slate-500 disabled:active:scale-100 sm:h-9 sm:w-9 sm:rounded-lg"
            >
              {ask.isPending ? <Spinner /> : <SendIcon size="sm" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-600">
          {turns.length > 0 && (
            <button
              type="button"
              onClick={() => clear.mutate()}
              disabled={clear.isPending}
              className="text-slate-500 underline-offset-2 transition-colors hover:text-slate-300 hover:underline"
            >
              Clear chat
            </button>
          )}
          <span className="ml-auto hidden sm:block">
            Enter to send · Shift+Enter for a new line
          </span>
        </div>
      </div>
    </AssistantHost.Provider>
  );
}

/**
 * The always-available way in: a button in the top bar that opens the general
 * assistant. Renders nothing when the deployment has no key, so the bar does
 * not carry a control that cannot work.
 */
export function AssistantLauncher({ context }: { context?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <AssistantOnly>
      <IconButton label="Ask Itsappy AI" onClick={() => setOpen(true)}>
        <SparkleIcon />
      </IconButton>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Itsappy AI"
        description="Ask about the work in front of you — or say what you want done."
        width="lg"
      >
        <AssistantAsk context={context} onDismiss={() => setOpen(false)} />
      </Modal>
    </AssistantOnly>
  );
}

export { ChatAssistant } from './ChatAssistant';
